import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
    type AvailabilityProduct,
    type BookingPriceSummary,
    type PaxData,
    SourceType,
    useComputePackagePrice,
} from '@travelity/api/services';
import { Stack } from '@mui/material';
import { getMinPriceFromDualCurrencyItems } from '@/utils/getMinPriceFromDualCurrencyItems';
import type { PackageBookingFormValues } from '../../package-form.helpers';
import {
    buildPackageItemsMeta,
    toComputePackagePriceDto,
} from '../../package-form.helpers';
import { PackagePriceSummary } from './components/PackagePriceSummary';
import { PackageBookingFooterControls } from './components/PackageBookingFooterControls';

interface PackageBookingFooterProps {
    selectedEvent: AvailabilityProduct;
    pax: PaxData;
    isSaving: boolean;
    onDiscard: () => void;
    onPriceResolved?: (beforeDiscount?: number) => void;
}

const PackageBookingFooter = ({
    selectedEvent,
    pax,
    isSaving,
    onDiscard,
    onPriceResolved,
}: PackageBookingFooterProps) => {
    const { control, formState, getValues } =
        useFormContext<PackageBookingFormValues>();
    const hasRequestedInitialPriceRef = useRef(false);

    const watchedPricingFields = useWatch({
        control,
        name: [
            'package_product_id',
            'date',
            'selectedTime',
            'pax',
            'customer',
            'sourceType',
            'sourceName',
            'referral',
            'discount',
            'vehicleByProductId',
            'selectedOptionsByItemKey',
        ],
    });

    const [
        packageProductId,
        date,
        selectedTime,
        selectedPax,
        customer,
        sourceType,
        sourceName,
        referral,
        discount,
        vehicleByProductId,
        selectedOptionsByItemKey,
    ] = watchedPricingFields;

    const computePriceParams = useMemo(
        () =>
            toComputePackagePriceDto(
                {
                    ...getValues(),
                    package_product_id: packageProductId,
                    date,
                    selectedTime,
                    pax: selectedPax,
                    customer,
                    discount,
                    vehicleByProductId,
                    selectedOptionsByItemKey,
                },
                selectedEvent
            ),
        [
            customer,
            date,
            discount,
            getValues,
            packageProductId,
            selectedEvent,
            selectedOptionsByItemKey,
            selectedPax,
            selectedTime,
            vehicleByProductId,
        ]
    );

    const {
        data: packagePrice,
        isLoading: isPriceLoading,
        changed: priceChanged,
        update: updatePrice,
        error: priceError,
    } = useComputePackagePrice(computePriceParams);

    useEffect(() => {
        hasRequestedInitialPriceRef.current = false;
    }, [selectedEvent.id]);

    useEffect(() => {
        if (!computePriceParams || hasRequestedInitialPriceRef.current) return;

        hasRequestedInitialPriceRef.current = true;
        updatePrice();
    }, [computePriceParams, updatePrice]);

    const errorFields = Object.values(formState.errors)
        .map((error) => error?.message)
        .filter((v) => v) as string[];

    const packageItemsMeta = useMemo(
        () => buildPackageItemsMeta(selectedEvent),
        [selectedEvent]
    );

    const pricing = useMemo<BookingPriceSummary | undefined>(() => {
        const financials = packagePrice?.financials;
        if (!financials) return undefined;
        console.log(financials, 'fin');
        const optionCurrency = packageItemsMeta
            .filter((item) => String(item.time) === selectedTime)
            .flatMap((item) =>
                item.optionsCatalog.map((option) =>
                    option.pricing?.items?.length
                        ? getMinPriceFromDualCurrencyItems(option.pricing.items)
                              .currency
                        : typeof option.pricing?.currency === 'string'
                        ? option.pricing.currency
                        : option.pricing?.currency?.abbr
                )
            )
            .find(Boolean);

        return {
            currency: optionCurrency || '',
            final: financials.total.original as number,
            beforeDiscount: financials.subtotal.original as number,
            discount: discount?.amount ? discount : undefined,
            pax: selectedPax || pax,
            options: [],
        };
    }, [
        discount,
        packageItemsMeta,
        packagePrice,
        pax,
        selectedPax,
        selectedTime,
    ]);

    useEffect(() => {
        onPriceResolved?.(pricing?.beforeDiscount);
    }, [onPriceResolved, pricing?.beforeDiscount]);

    const hasPriceChanges = formState.isDirty && priceChanged;

    const submitDisabled =
        !!errorFields.length ||
        !customer?.id ||
        (sourceType === SourceType.DIRECT
            ? !sourceName
            : !referral?.id && !referral?.email) ||
        formState.isSubmitting ||
        isSaving ||
        isPriceLoading ||
        hasPriceChanges ||
        !!priceError ||
        !pricing;

    return (
        <Stack
            sx={{
                width: 1,
                zIndex: 1,
                mt: 'auto',
                pr: 1.5,
                pb: 2,
                pt: 2,
                bgcolor: '#FFF',
                borderTop: '1px solid #E5E7EB',
            }}
        >
            <PackagePriceSummary
                pricing={pricing}
                pax={selectedPax || pax}
                isLoading={isPriceLoading}
                priceChanged={hasPriceChanges}
                updatePrice={updatePrice}
            />
            <PackageBookingFooterControls
                submitDisabled={submitDisabled}
                onDiscard={onDiscard}
            />
        </Stack>
    );
};

export default PackageBookingFooter;
